import { useState, useEffect } from 'react';

import ChevroneRightIcon from '@common/assets/icons/chevrone_right.svg?react';
import { Card } from '@common/ui/Card';
import { Cell, type CellProps } from '@common/ui/Cell';
import { Checkbox } from '@common/ui/Checkbox';
import { Modal } from '@common/ui/Modal';
import { Separator } from '@common/ui/Separator';

import styles from './MultiSelect.module.scss';

export interface Item {
  value: string;
  label: string;
}

export interface MultiSelectProps
  extends Omit<CellProps, 'suffixIcon' | 'onChange' | 'subtitle' | 'testId'> {
  items: Item[];
  value?: string[];
  onChange?: (value: string[]) => void;
}

export const MultiSelect = ({
  items,
  value,
  onChange,
  ...props
}: MultiSelectProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    if (value !== undefined) {
      setSelected(value);
    } else {
      setSelected([]);
    }
  }, [value]);

  const onChangeHandler = (value: string) => {
    let newValue = selected;

    if (selected.includes(value)) {
      newValue = newValue.filter((item) => item !== value);
    } else {
      newValue = [...selected, value];
    }

    setSelected(newValue);
    onChange?.(newValue);
  };

  let subtitle = undefined;
  if (selected.length) {
    subtitle = items
      .filter((item) => selected.includes(item.value))
      .map((item) => item.label)
      .join(', ');
    if (subtitle.length > 40) {
      subtitle = subtitle.slice(0, 40) + '...';
    }
  }

  return (
    <div className={styles.multiSelect}>
      <Cell
        {...props}
        testId="multi-select-trigger"
        subtitle={subtitle}
        suffixIcon={
          <span className={styles.suffix}>
            {selected.length > 0 && (
              <div className={styles.itemsCount}>{selected.length}</div>
            )}
            <ChevroneRightIcon />
          </span>
        }
        onClick={() => setOpen(true)}
      />
      <Modal
        testId="multi-select-modal"
        className={styles.modal}
        open={open}
        onClose={() => setOpen(false)}
      >
        <Card testId="multi-select-items" className={styles.card}>
          {items.map((item, index) => (
            <Cell
              key={item.value}
              testId={`multi-select-option-${item.value}`}
              title={item.label}
              separator={index < items.length - 1 ? <Separator /> : undefined}
              suffixIcon={
                <Checkbox
                  checked={selected.includes(item.value)}
                  testId={`multi-select-checkbox-${item.value}`}
                  onClick={() => onChangeHandler(item.value)}
                />
              }
            />
          ))}
        </Card>
      </Modal>
    </div>
  );
};
